<template>
  <div>
    <div class="row">
      <template v-if="editing">
        <div class="col-3">
          <DaySelect :history="false" :today="true" :future="true" @configChanged="config = $event"
                     :currentConfig="config"/>
        </div>
        <div class="col-3">
          <ActivitySelect @configChanged="config = $event" :currentConfig="config"/>
        </div>
        <div class="col-3">
          <IncludeWeekends @configChanged="config = $event" :currentConfig="config"></IncludeWeekends>
        </div>
        <div class="col-3">
          <label for="title-planning">{{ $t('ui.title') }}</label>
          <input type="text" id="title-planning" class="form-control" v-model="config.title">
        </div>
        <div class="col-3">
          <NormPerDayTime @configChanged="config = $event" :currentConfig="config"></NormPerDayTime>
        </div>
        <div class="col-3">
          <NormPerDayAmount @configChanged="config = $event" :currentConfig="config"></NormPerDayAmount>
        </div>
        <div class="col-6 align-right align-vertical-bottom">
          <AmountViewToggle @configChanged="config = $event" :currentConfig="config"></AmountViewToggle>
        </div>
      </template>
    </div>
    <div class="row" v-if="!editing">
      <div class="col-12 align-right">
        <AmountViewToggle @configChanged="config = $event" :currentConfig="config"></AmountViewToggle>
      </div>
    </div>
    <div class="chart-container" v-if="!editing">
      <component-load-spinner :loading="loading"></component-load-spinner>
      <bar-chart v-if="getAnnotations()"
                 style="width: 100%; height: 100%"
                 :style="loading ? 'opacity:0.5' : 'opacity:1'"
                 :class="loading ? 'loading':''"
                 :chartData="getChartData"
                 :options="{ ...getOptions, ...getAnnotations() }"/>
    </div>
  </div>


</template>

<script>
import {mapGetters} from "vuex";

import DaySelect from "@/components/insights/forms/DaySelect";
import BarChart from '@/components/insights/charts/barChart.js'

import timeCalc from "@/mixins/timeCalc";
import generalMethods from "@/mixins/insights/generalMethods";
import chartColors from "@/mixins/insights/chartColors";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ActivitySelect from "@/components/insights/forms/ActivitySelect";
import IncludeWeekends from "@/components/insights/forms/IncludeWeekends";
import AmountViewToggle from "@/components/insights/forms/AmountViewToggle";
import NormPerDayTime from "@/components/insights/forms/NormPerDayTime";
import NormPerDayAmount from "@/components/insights/forms/NormPerDayAmount";

import timespan from "@/mixins/timespan";
import ComponentLoadSpinner from "@/components/general/ComponentLoadSpinner";

import timeUtils from 'mout/time';

export default {
  components: {
    ComponentLoadSpinner,
    IncludeWeekends,
    ActivitySelect,
    DaySelect,
    BarChart,
    AmountViewToggle,
    NormPerDayTime,
    NormPerDayAmount
  },
  mixins: [timeCalc, generalMethods, chartColors, timespan],
  props: ['editing'],
  firestore() {
    return {
      plannedJobs: this.getCollectionPlannedJobs(),
      finishedJobs: this.getCollectionFinishedJobs(),
      originallyPlannedJobs: this.getCollectionOriginallyPlannedJobs(),
    }
  },
  data() {
    return {
      plannedJobs: [],
      finishedJobs: [],
      originallyPlannedJobs: [],
      loading: true,
      pageRendered: false,
      config: {
        dayDifference: {start: 0, end: 0},
        activity: null,
        includeWeekends: false,
        includeDutchHolidays: false,
        amountView: true, // true = amount, false = time
        normPerDayInTime: {
          unit: 'H',
          value: 0
        },
        normPerDayAmount: 0
      }
    }
  },
  computed: {
    ...mapGetters('users', ['getClaims']),
    getPlannedJobs() {
      return this.plannedJobs
    },
    getFinishedJobs() {
      return this.finishedJobs
    },
    getOptions() {
      const self = this

      let viewSpecificOptions = {
        yAxes: {
          ticks: {}
        },
        tooltips: {
          callbacks: {}
        }
      };
      if (this.config.amountView) {
        viewSpecificOptions.tooltips.callbacks.title = function (tooltip) {
          return tooltip[0].label
        };
        viewSpecificOptions.tooltips.callbacks.label = function (tooltipItem) {
          return tooltipItem.value
        };
      } else {
        viewSpecificOptions.yAxes.ticks.stepSize = ((1000 * 60 * 60)) * 5;
        viewSpecificOptions.yAxes.ticks.callback = function (label) {
          return self.getHoursFromMilliseconds(label, false) + ' hour(s)'
        };
        viewSpecificOptions.tooltips.callbacks.title = function (tooltip) {
          return moment(tooltip[0].label, 'DD-MM-YYYY').format('DD MMM')
        };
        viewSpecificOptions.tooltips.callbacks.label = function (tooltipItem) {
          return self.formattedColonTime(tooltipItem.value) + ' hour(s)'   //tooltipItem.value
        };
      }
      return {
        ...this.generalChartOptions,
        animation: {
          onComplete: () => {

            self.loading = false;
          }
        },
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false
              },
              // bounds: 'ticks',
              offset: true, // IF this is set to false, it will not display the last bar!
              stacked: true,
              autoSkip: false,
              startAt: 0,
            },
          ],
          yAxes: [{
            ticks: {
              beginAtZero: true,
              autoSkip: true,
              type: 'linear',
              ...viewSpecificOptions.yAxes.ticks
            },
          }],
        },
        tooltips: {
          ...viewSpecificOptions.tooltips
        },
        plugins: {
          datalabels: {
            color: 'black',
            anchor: 'start',
            align: 'bottom',
            font: {
              // weight: 'bold',
              size: 20
            },
            offset: -50,

            display: function (ctx) {
              return false
              return ctx.datasetIndex > 0 //ctx.chart.$totalizer.utmost;
            },
            formatter: (value, ctx) => {
              return self.getHoursFromMilliseconds(value.y, false) > 0 ? self.getHoursFromMilliseconds(value.y, false) + '' : ' '
            },
          }
        },
        responsive: true,
        maintainAspectRatio: false,
      }
    },
     getFinishedJobsGroupedByFinishedDate() {
      return _.groupBy(this.getFinishedJobs, job => {
        return moment(job.finishedDate.toDate()).startOf('day').format('YYYY-MM-DD')
      })
    },
    getPlannedJobsGroupedByPlanDate() {
      return _.groupBy(this.getPlannedJobs, job => {
        return moment(job.plannedDate.toDate()).startOf('day').format('YYYY-MM-DD')
      })
    },
    getChartDataFinishedJobsFinishedOnPlanDate() {
      let returnValues = [];
      if (this.config.amountView === true) {
        const dates = this.getDatesOfChart();
        dates.forEach(date => {
          const jobsOfDate = this.getFinishedJobsGroupedByFinishedDate[moment(date).format('YYYY-MM-DD')]
          if (jobsOfDate) {
            const sum = _.sumBy(jobsOfDate, 'amount');
            returnValues.push(sum ? sum : 0);
          } else {
            returnValues.push(0);
          }
        });
      } else {
        const dates = this.getDatesOfChart();
        dates.forEach(date => {
          const jobsOfDate = this.getFinishedJobsGroupedByFinishedDate[moment(date).format('YYYY-MM-DD')];

          if (jobsOfDate) {
            returnValues.push(_.sumBy(jobsOfDate, 'timeSpent'));
          } else {
            returnValues.push(0);
          }
        });
      }
      return returnValues;
    },
    getPlannedJobsFinishedOnTime: (vue) => (date) => {
      const jobsPlannedOnDate = vue.getPlannedJobsGroupedByPlanDate[moment(date).format('YYYY-MM-DD')];
      if (jobsPlannedOnDate) {
        let priorJobsFinishedOnTime = _.filter(jobsPlannedOnDate, jobPlannedOnDate => {
          if (jobPlannedOnDate.status === 'finished') {
            // If the current job is already finished it shouldn't be on the left side
            return false;
          } else if (!jobPlannedOnDate.previousJobId) {
            // No prior job?  Then it's finished on time
            return true;
          } else if (!jobPlannedOnDate.previousJobFinished) {
            // If the prior job is not finished it's too late
            return false;
          } else if (moment(jobPlannedOnDate.plannedDate.toDate()).isSame(jobPlannedOnDate.originalPlannedDate.toDate(), 'date')) {
            // If it's  planned on the same date as the origional date => then it's on time
            return true;
          } else {
            // If it's not finished on the same date as the plan date => then it's too late
            return false;
          }
        });
        return priorJobsFinishedOnTime;
      } else {
        return [];
      }
    },
    getPlannedJobsFinishedTooLate: (vue) => (date) => {
      const jobsPlannedOnDate = vue.getPlannedJobsGroupedByPlanDate[moment(date).format('YYYY-MM-DD')];
      if (jobsPlannedOnDate) {
        let priorJobsFinishedOnTooLate = _.filter(jobsPlannedOnDate, jobPlannedOnDate => {
          if (jobPlannedOnDate.status === 'finished') {
            // If the current job is already finished it shouldn't be on the left side
            return false;
          } else if (!jobPlannedOnDate.previousJobId) {
            // No prior job?  Then it's finished on time
            return true;
          } else if (!jobPlannedOnDate.previousJobFinished) {
            // If the prior job is not finished it's too late
            return false;
          } else if (moment(jobPlannedOnDate.plannedDate.toDate()).isSameOrBefore(jobPlannedOnDate.originalPlannedDate.toDate(), 'date')) {
            // If it's finished on or before the original plan date => its too early or on time
            return false;
          } else {
            // If it's finished after the original date => then it's too late
            return true;
          }
        });
        return priorJobsFinishedOnTooLate;
      } else {
        return [];
      }
    },
    getPlannedJobsFinishedTooEarly: (vue) => (date) => {
      const jobsPlannedOnDate = vue.getPlannedJobsGroupedByPlanDate[moment(date).format('YYYY-MM-DD')];
      if (jobsPlannedOnDate) {
        let priorJobsFinishedTooEarly = _.filter(jobsPlannedOnDate, jobPlannedOnDate => {
          if (jobPlannedOnDate.status === 'finished') {
            // If the current job is already finished it shouldn't be on the left side
            return false;
          } else if (!jobPlannedOnDate.previousJobId) {
            // No prior job?  Then it's finished on time
            return false;
          } else if (!jobPlannedOnDate.previousJobFinished) {
            // If the prior job is not finished it's too late
            return false;
          } else if (moment(jobPlannedOnDate.plannedDate.toDate()).isSameOrAfter(jobPlannedOnDate.originalPlannedDate.toDate(), 'date')) {
            // If it's finished on or before the original plan date => its too late or on time
            return false;
          } else {
            // If it's finished after the original date => then it's too early
            return true;
          }
        });
        return priorJobsFinishedTooEarly;
      } else {
        return [];
      }
    },
    getPlannedJobsNotFinished: (vue) => (date) => {
      if (!vue.plannedJobs) {
        return [];
      }
      const plannedForTodayOrEarlier = _.filter(vue.originallyPlannedJobs, job => {
        return moment(job.originalPlannedDate.toDate()).isSameOrBefore(date, 'date');
      });


      return _.filter(plannedForTodayOrEarlier, jobPlannedOnDate => {
            if (jobPlannedOnDate.status === 'finished') {
              // If the current job is already finished it shouldn't be on the left side
              return false;
            } else if (!jobPlannedOnDate.previousJobId) {
              // No prior job?  Then it's also not late
              return false;
            } else {
              return !jobPlannedOnDate.previousJobFinished;
            }
      });
    },
    getChartDataPriorJobFinishedOnTime() {
      let returnValues = [];
      if (this.config.amountView === true) {
        const dates = this.getDatesOfChart();
        dates.forEach(date => {
          const jobsFinishedOnTime = this.getPlannedJobsFinishedOnTime(date);

          const sum = _.sumBy(jobsFinishedOnTime, 'amount');
          returnValues.push(sum ? sum : 0);
        });
      } else {
        const dates = this.getDatesOfChart();
        dates.forEach(date => {
          const jobsFinishedOnTime = this.getPlannedJobsFinishedOnTime(date);
          returnValues.push(_.sumBy(jobsFinishedOnTime, 'target'));
        });
      }
      return returnValues;
    },
    getChartDataPriorJobFinishedTooLate() {
      let returnValues = [];
      if (this.config.amountView === true) {
        const dates = this.getDatesOfChart();
        dates.forEach(date => {
          const jobsFinishedTooLate = this.getPlannedJobsFinishedTooLate(date);
          const sum = _.sumBy(jobsFinishedTooLate, 'amount');
          returnValues.push(sum ? sum : 0);
        });
      } else {
        const dates = this.getDatesOfChart();
        dates.forEach(date => {
          const jobsFinishedTooLate = this.getPlannedJobsFinishedTooLate(date);
          returnValues.push(_.sumBy(jobsFinishedTooLate, 'target'));
        });
      }
      return returnValues;
    },
    getChartDataPriorJobFinishedTooEarly() {
      let returnValues = [];
      if (this.config.amountView === true) {
        const dates = this.getDatesOfChart();
        dates.forEach(date => {
          const jobsFinishedTooEarly = this.getPlannedJobsFinishedTooEarly(date);
          const sum = _.sumBy(jobsFinishedTooEarly, 'amount');
          returnValues.push(sum ? sum : 0);
        });
      } else {
        const dates = this.getDatesOfChart();
        dates.forEach(date => {
          const jobsFinishedTooEarly = this.getPlannedJobsFinishedTooEarly(date);
          returnValues.push(_.sumBy(jobsFinishedTooEarly, 'target'));
        });
      }
      return returnValues;
    },
    getChartDataPriorJobNotFinishedOnTime() {
      let returnValues = [];
      if (this.config.amountView === true) {
        const dates = this.getDatesOfChart();
        dates.forEach(date => {
          const jobsNotFinishedOnTime = this.getPlannedJobsNotFinished(date);
          const sum = _.sumBy(jobsNotFinishedOnTime, 'amount');
          returnValues.push(sum ? sum : 0);
        });
      } else {
        const dates = this.getDatesOfChart();
        dates.forEach(date => {
          const jobsNotFinishedOnTime = this.getPlannedJobsNotFinished(date);
          returnValues.push(_.sumBy(jobsNotFinishedOnTime, 'target'));
        });
      }
      return returnValues;
    },
    getLabels() {
      const dates = this.getDatesOfChart();
      return _.map(dates, date => {
        return moment(date).format('DD-MM-YYYY')
      });
    },
    getChartData() {
      return {
        labels: this.getLabels,
        datasets: [
          {
            stack: 'Stack todo',
            label: this.$t('insights.jobProgress.readyLate'),
            backgroundColor: this.colors.jobProgress.readyLate,
            data: this.getChartDataPriorJobFinishedTooLate,
          },
          {
            stack: 'Stack todo',
            label: this.$t('insights.jobProgress.readyOnTime'),
            backgroundColor: this.colors.jobProgress.readyOnTime,
            data: this.getChartDataPriorJobFinishedOnTime,
          },
          {
            stack: 'Stack todo',
            label: this.$t('insights.jobProgress.readyEarly'),
            backgroundColor: this.colors.jobProgress.readyEarly,
            data: this.getChartDataPriorJobFinishedTooEarly,
          },
          {
            stack: 'Stack todo',
            label: this.$t('insights.jobProgress.notReadyAndLate'),
            backgroundColor: this.colors.jobProgress.notReadyAndLate,
            data: this.getChartDataPriorJobNotFinishedOnTime,
          },
          {
            stack: 'Stack done',
            label: this.$t('insights.jobProgress.finishedJob'),
            backgroundColor: this.colors.jobProgress.finishedJob,
            data: this.getChartDataFinishedJobsFinishedOnPlanDate,
          }
        ]
      }
    },
    getAnnotationValue() {
      if (this.config.amountView) {
        if (this.config.normPerDayAmount === null || this.config.normPerDayAmount === undefined) {
          return null;
        } else {
          return this.config.normPerDayAmount;
        }
      } else {
        if (!this.config.normPerDayInTime || this.config.normPerDayInTime.value === null || this.config.normPerDayInTime.value === undefined) {
          return null;
        }
        const value = this.config.normPerDayInTime.value;
        const unit = this.config.normPerDayInTime.unit;

        if (unit) {
          return timeUtils.convert(value, unit.toLowerCase(), 'ms');
        } else {
          return 0;
        }
      }
    }
  },
  methods: {
    getAnnotations() {
      return {
        annotation: {
          annotations: [
            {
              borderColor: this.colors.jobProgress.annotationColor,
              borderWidth: 2,
              mode: 'horizontal',
              type: 'line',
              value: this.getAnnotationValue,
              scaleID: 'y-axis-0'
            }
          ]
        }
      }
    },
    getDatesOfChart() {
      if (!this.config.dayDifference) {
        return []
      }
      return this.getDatesWithinRangeFromToday(this.config.dayDifference.start, this.config.dayDifference.end - this.config.dayDifference.start, this.config.includeWeekends, this.config.includeDutchHolidays);
    },
    getCollectionPlannedJobs() {
      let userClaims = this.getClaims;
      const dates = this.getDatesOfChart();
      let startDay = moment(_.first(dates)).startOf('day');
      let endDay = moment(_.last(dates)).endOf('day');
      return this.$FB.collection('jobs')
          .where('companyId', '==', userClaims.companyId)
          .where('plannedDate', '>=', startDay.toDate())
          .where('plannedDate', '<=', endDay.toDate())
          .where('activity', '==', this.config.activity)
    },
    getCollectionOriginallyPlannedJobs() {

      let userClaims = this.getClaims;
      const dates = this.getDatesOfChart();
      let endDay = moment(_.last(dates)).endOf('day');
      return this.$FB.collection('jobs')
          .where('companyId', '==', userClaims.companyId)
          .where('originalPlannedDate', '<=', endDay.toDate())
          .where('status', '==', 'planned')
          .where('activity', '==', this.config.activity)
    },
    getCollectionFinishedJobs() {
      let userClaims = this.getClaims;
      const dates = this.getDatesOfChart();
      let startDay = moment(_.first(dates)).startOf('day');
      let endDay = moment(_.last(dates)).endOf('day');
      return this.$FB.collection('jobs')
          .where('companyId', '==', userClaims.companyId)
          .where('finishedDate', '>=', startDay.toDate())
          .where('finishedDate', '<=', endDay.toDate())
          .where('activity', '==', this.config.activity)
    },
    updateData() {
      this.loading = true;
      // Unbind the data and rebind
      this.$unbind('finishedJobs')
      this.$bind('finishedJobs', this.getCollectionFinishedJobs());
      this.$unbind('plannedJobs')
      this.$bind('plannedJobs', this.getCollectionPlannedJobs());
      this.$unbind('originallyPlannedJobs')
      this.$bind('originallyPlannedJobs', this.getCollectionOriginallyPlannedJobs());
    }
  },
  created() {
    setTimeout(() => {
      this.pageRendered = true;
      this.updateData();
    }, 500);
  },
  watch: {
    'config.dayDifference': {
      handler(newValue, oldValue) {
        if (!_.isEqual(newValue, oldValue) && this.pageRendered) {
          this.updateData();
        }
      },
      deep: true
    },
    'config.activity'(newValue, oldValue) {
      if (!_.isEqual(newValue, oldValue) && this.pageRendered) {
        this.updateData();
      }
    },
    'config.includeWeekends'(newValue, oldValue) {
      if (!_.isEqual(newValue, oldValue) && this.pageRendered) {
        this.updateData();
      }
    },
    'config.includeDutchHolidays'(newValue, oldValue) {
      if (!_.isEqual(newValue, oldValue) && this.pageRendered) {
        this.updateData();
      }
    },
  }
}
</script>

<style scoped lang="scss">
@import "./src/assets/scss/components/insights/_generalStylingCharts.scss";

.align-right {
  text-align: right;
}

.align-vertical-bottom {
  align-self: end;
}

</style>